import { UserProfile, UserSelector, UserTableView } from '@features/users';
import { selectGetAllUsersStatus, selectUsers } from '@features/users/store/selectors';
import { User } from '@features/users/types';
import { ColumnFilters, RequestStatusEnum } from '@shared/types';
import { Dispatch, SetStateAction } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { useUserFilter } from './useUserFilter';

interface CreatedByUserColumnOptions {
    columnFilters: ColumnFilters;
    setColumnFilters: Dispatch<SetStateAction<ColumnFilters>>;
    size?: number;
    header?: string;
    accessorKey?: string;
}

export const useCreatedByUserColumn = ({
    columnFilters,
    setColumnFilters,
    size,
    accessorKey,
    header,
}: CreatedByUserColumnOptions) => {
    const { t } = useTranslation();
    const users = useSelector(selectUsers);
    const getAllUsersStatus = useSelector(selectGetAllUsersStatus);
    const { userId, handleChangeUserFilter, handleClearUserFilter } = useUserFilter({
        columnFilters,
        setColumnFilters,
        accessorKey,
    });

    return {
        accessorKey: accessorKey ?? 'created_by_user',
        header: header ? t(header) : t('created_by'),
        enableSorting: false,
        size: size ?? 171,
        Cell: ({ cell }: any) => <UserTableView showPopover userId={cell.getValue() as number} />,
        filterVariant: 'autocomplete' as const,
        filterSelectOptions:
            getAllUsersStatus !== RequestStatusEnum.Loading
                ? users.map((item) => ({ label: `${item.first_name} ${item.last_name}`, value: item.pk }))
                : [],
        Filter: () => (
            <UserSelector
                sx={{ width: '100%' }}
                value={userId}
                onChange={(_event, newOption) =>
                    newOption ? handleChangeUserFilter(newOption.pk) : handleClearUserFilter()
                }
                renderOption={(props: any, option: User) => (
                    <UserProfile
                        key={option.pk}
                        createdUserId={option.pk}
                        showAvatar={false}
                        userContainerProps={{ sx: { border: 'none', gap: '2px', margin: 0, width: '100%' }, ...props }}
                    />
                )}
                TextFieldProps={{ variant: 'standard', label: null }}
                inputHeight={34}
            />
        ),
    };
};
